import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Loader2, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { GlassCard } from '../components/shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12">
        <GlassCard className="max-w-md w-full text-center">
          <h2 className="text-xl font-serif text-text-primary mb-3">Lien manquant</h2>
          <p className="text-text-secondary text-sm mb-4">
            Ouvrez le lien reçu par email ou demandez un nouveau lien.
          </p>
          <Link to="/forgot-password" className="text-primary hover:underline">Demander un nouveau lien</Link>
        </GlassCard>
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) { setError("Mot de passe trop court (6 caractères minimum)."); return; }
    if (password !== confirm) { setError("Les mots de passe ne correspondent pas."); return; }
    setLoading(true);
    // Hide keyboard before submitting to improve UX
    document.activeElement?.blur();
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2200);
    } catch (err) {
      setError(err?.response?.data?.detail || "Une erreur est survenue.");
      // Re-show keyboard on error by refocusing the password input
      setTimeout(() => document.getElementById('password')?.focus(), 100);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/logo-192.png" alt="Noor El Bayen" className="w-16 h-16 mx-auto mb-4 rounded-xl" draggable="false" />
          <h1 className="text-2xl font-serif text-text-primary mb-2">Nouveau mot de passe</h1>
        </div>

        <GlassCard>
          {done ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-3" />
              <h3 className="text-lg font-serif text-text-primary mb-2">Mot de passe mis à jour</h3>
              <p className="text-text-secondary text-sm">Redirection vers la connexion…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-danger/20 border border-danger/50 text-danger text-sm" data-testid="reset-error">{error}</div>
              )}
              <div className="space-y-2">
                <Label htmlFor="pwd" className="text-text-primary">Nouveau mot de passe</Label>
                <div className="relative">
                  <Input id="pwd" type={showPwd ? 'text' : 'password'} required
                         value={password} onChange={(e) => setPassword(e.target.value)}
                         className="bg-surface border-border pr-10"
                         data-testid="reset-password-input" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm" className="text-text-primary">Confirmer</Label>
                <Input id="confirm" type="password" required
                       value={confirm} onChange={(e) => setConfirm(e.target.value)}
                       className="bg-surface border-border"
                       data-testid="reset-confirm-input" />
              </div>
              <Button type="submit" disabled={loading}
                      className="w-full bg-primary text-background hover:bg-primary-hover"
                      data-testid="reset-submit-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Mettre à jour
              </Button>
            </form>
          )}
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
